import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from 'chart.js';
import { PageContent, PageSection, Subtitle } from 'common/styles/page';
import styled from 'styled-components';
import DashboardPillarRadar from './DashboardPillarRadar';
import DashboardTeam from './DashboardTeam';

import DashboardCopilotLeft from './DashboardCopilotLeft';
import DashboardGreatestOpportunities from './DashboardGreatestOpportunities';
import DashboardGreatestStrengths from './DashboardGreatestStrengths';
import DashboardMainCharts from './DashboardMainCharts';
import DashboardPillarScoresLeft from './DashboardPillarScoresLeft';
import DashboardSentiment from './DashboardSentiment';

import { assessmentPeriodApi } from 'areas/assessment-schedule/api/assessmentPeriodApi';
import ToggleControl from 'common/components/ToggleChartControl';

import LandingPage from 'common/layouts/AuthLayout/LandingPage';
import DashboardAssessmentProgressRight from './DashboardAssessmentProgressRight';
import DashboardAssessmentProgressLeft from './DashboardAssessmentProgressLeft';
import DashboardOutcomes from './DashboardOutcomes';
import DashboardSurveysCompleted from './DashboardSurveyComplete';

import LoadingSpinner from 'common/components/LoadingSpinner';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import { SemanticICONS } from 'semantic-ui-react';
import {
  getAssessmentPeriods,
  getCurrentOrganizationId,
  getMostRecentAssessmentPeriod,
  getPreviousAssessmentPeriodId,
  getSelectedAssessmentPeriod,
} from '../../../common/selectors/auth';
import { ASSESSMENT_PERIOD_CLOSED, ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_OPEN } from 'areas/assessment-schedule/constants';

const DashboardMain = () => {
  ChartJS.register(ArcElement, BarElement, Tooltip, Legend, CategoryScale, LinearScale);
  ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

  const assessmentPeriod = useSelector(getMostRecentAssessmentPeriod);
  const previousAssessmentPeriodId = useSelector(getPreviousAssessmentPeriodId);
  const organizationId = useSelector(getCurrentOrganizationId);
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const currentAssessmentPeriod = selectedAssessmentPeriod ?? assessmentPeriod;
  const isProgressPeriod =
    currentAssessmentPeriod?.status === ASSESSMENT_PERIOD_OPEN ||
    currentAssessmentPeriod?.status === ASSESSMENT_PERIOD_IN_PROGRESS;
  const [view, setView] = useState<'progress' | 'main' | 'team'>(
    isProgressPeriod ? 'progress' : 'main'
  );
  const initialViewSet = React.useRef(false);

  const { setTopBarContent } = useContext(TopBarContext);

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

  const assessmentPeriods = useSelector(getAssessmentPeriods);

  const isTeamEnabled = currentAssessmentPeriod?.status === ASSESSMENT_PERIOD_CLOSED;

  const { data: assessment_progress_data, isSuccess: isProgressSuccess } =
    assessmentPeriodApi.useApiPortalV1AssessmentsCompletedRetrieveQuery(
      { assessmentPeriodId: currentAssessmentPeriod?.id ?? 0 },
      { skip: !currentAssessmentPeriod }
    );

  useEffect(() => {
    if (assessmentPeriod ? isProgressSuccess : true) {
      setIsDataLoaded(true);
    }
  }, [assessmentPeriod, assessmentPeriods, assessment_progress_data, isProgressSuccess]);

 
  useEffect(() => {
    if (!currentAssessmentPeriod) return;
      setView(
        currentAssessmentPeriod.status === ASSESSMENT_PERIOD_OPEN ||
        currentAssessmentPeriod.status === ASSESSMENT_PERIOD_IN_PROGRESS
          ? 'progress'
          : view
      );
  }, [currentAssessmentPeriod]);

  useEffect(() => {
    if (view === 'team' && !isTeamEnabled) {
      setView('main');
    }
  }, [view, isTeamEnabled]);

  useEffect(() => {
    const toggleButtons: { type: string; icon: SemanticICONS; disabled: boolean }[] = [
      {
        type: 'progress',
        icon: 'clock',
        disabled: !currentAssessmentPeriod,
      },
      {
        type: 'main',
        icon: 'chart bar',
        disabled: currentAssessmentPeriod?.status !== ASSESSMENT_PERIOD_CLOSED,
      },
      {
        type: 'team',
        icon: 'users',
        disabled: !isTeamEnabled,
      },
    ];

    
    setTopBarContent(
      <TopBarControls
        controls={
          <ToggleControl
            buttonLabels={toggleButtons}
            activeToggle={view}
            setChartType={val => setView(val as 'progress' | 'main' | 'team')}
            size="mini"
          />
        }
        status={[ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_CLOSED, ASSESSMENT_PERIOD_OPEN]}
      />
    );
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId, view, currentAssessmentPeriod]);

  if (!isDataLoaded) {
    return <LoadingSpinner />;
  }

  if (!currentAssessmentPeriod) {
    return (
      <PageContent>
        <LandingPage
          title="Welcome to nuLogic!"
          subtitle="Import members and schedule an assessment to collect organization sentiment data..."
          buttonLabel="Let's get started!"
          navDestination="/app/assessments"
        />
      </PageContent>
    );
  }

  return (
    <PageContent data-testid="dashboard">
      <PageSection>
        {view === 'progress' && assessment_progress_data && (
          <PageSection
            width="100%"
            display="flex"
            alignItems="stretch"
            gap="15px"
            style={{ height: '100%' }}
            data-testid="admin-dashboard-progress"
          >
            <LeftColumn style={{ width: '300px', minWidth: '300px', maxWidth: '300px' }}>
              <StyledCard $cursor="auto">
                <DashboardAssessmentProgressLeft
                  assessment_progress_data={assessment_progress_data}
                />
              </StyledCard>
            </LeftColumn>
            <RightColumn>
              <PageSection>
                <StyledCard $cursor="auto">
                  <PageSection padtop padleft padright padbottom style={{ height: '580px' }}>
                    <Subtitle>Member Participation</Subtitle>
                    <DashboardAssessmentProgressRight
                      height="500px"
                      assessment_progress_data={assessment_progress_data as any}
                      start_date={new Date(currentAssessmentPeriod?.startDateTime || '')}
                      end_date={new Date(currentAssessmentPeriod?.endDateTime || '')}
                    />
                  </PageSection>
                </StyledCard>
              </PageSection>
            </RightColumn>
          </PageSection>
        )}

        {view === 'main' && currentAssessmentPeriod.status === ASSESSMENT_PERIOD_CLOSED && (
          <>
            <DashboardMainSection data-testid="admin-dashboard-main">
              <LeftColumn style={{ width: '435px', minWidth: '435px' }}>
                <StyledCard>
                  <DashboardPillarScoresLeft
                    assessment_period_id={currentAssessmentPeriod.id}
                    prior_assessment_period_id={previousAssessmentPeriodId}
                  />
                </StyledCard>
                <StyledCard>
                  <DashboardCopilotLeft assessment_period_id={currentAssessmentPeriod.id} />
                </StyledCard>
              </LeftColumn>
              <RightColumn>
                <PageSection>
                  <StyledCard>
                    <DashboardMainCharts organization_id={organizationId} />
                  </StyledCard>
                </PageSection>
              </RightColumn>
            </DashboardMainSection>

            <CardContainer data-testid="admin-dashboard-cards">
              <DashboardGreatestStrengths assessment_period_id={currentAssessmentPeriod.id} />
              <DashboardGreatestOpportunities assessment_period_id={currentAssessmentPeriod.id} />
              <DashboardSentiment assessment_period_id={currentAssessmentPeriod.id} />
              <DashboardPillarRadar
                prior_assessment_period_id={previousAssessmentPeriodId}
                assessment_period_id={currentAssessmentPeriod.id}
              />
              <DashboardOutcomes assessment_period_id={currentAssessmentPeriod.id} />
              <DashboardSurveysCompleted assessment_period_id={currentAssessmentPeriod.id} />
            </CardContainer>
          </>
        )}

        {view === 'team' && currentAssessmentPeriod && (
          <PageSection>
            <DashboardTeam />
          </PageSection>
        )}
      </PageSection>
    </PageContent>
  );
};
export default DashboardMain;

// Styled Components
const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 15px;
  padding-top: 15px;
  height: 100%;
  max-width: 1600px;
  margin: 0 auto;
`;

const DashboardMainSection = styled(PageSection)`
  width: 100%;
  display: flex;
  align-items: stretch;
  height: 590px;
  max-width: 1600px;
  margin: 0 auto;

  @media (max-width: 1040px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 15px;

  @media (max-width: 1040px) {
    width: 100% !important;
    min-width: 0 !important;
    padding-right: 0;
    margin-bottom: 10px;
  }
`;

const RightColumn = styled.div`
  flex: 2 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;

  gap: 10px;

  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const StyledCard = styled.div<{ $cursor?: string }>`
  flex-grow: 1;
  border-radius: 6px;
  padding: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
  display: flex; /* Use flex for better layout control */
  flex-direction: column; /* Ensure contents stack vertically */

  // Prevent contents from overflowing
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  /* Allow optional cursor override (default: auto; hover default: pointer) */
  cursor: ${props => props.$cursor || 'auto'};

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    cursor: ${props => props.$cursor || 'pointer'};
  }
`;


